import React, { useState, useCallback, useContext } from 'react';
import PropTypes from 'prop-types';

import { Menu, MenuItem } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';

import ConfirmDialog from '../../../../CoachTools/components/ConfirmDialog';
import { ClientDocumentSource } from '../../../../chat/Model/ClientDocument';
import LoggedInUserContext from '../../../../context/LoggedInUserContext';
import useToast from '../../../../chat/hooks/useToast';
import {
  MenuCell,
  MenuIconButton,
  RenameDoneButton,
  EditIcon,
  SaveIcon,
  ArchiveIcon,
  TrashIcon,
  DeleteText,
} from './styles';
import texts from './texts.json';

const DocumentItemMenu = ({
  document,
  isRenaming,
  onRenameStart,
  onRenameSubmit,
  deleteTexts,
}) => {
  const { isSaved, source } = document;
  const { userDoc: loggedInUserDoc } = useContext(LoggedInUserContext);
  const { showToast } = useToast();
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [pendingRename, setPendingRename] = useState(false);

  const handleMenuOpen = useCallback((event) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
  }, []);

  const handleMenuClose = useCallback(() => setMenuAnchor(null), []);

  const handleMenuExited = useCallback(() => {
    if (pendingRename) {
      setPendingRename(false);
      onRenameStart();
    }
  }, [pendingRename, onRenameStart]);

  const handleRename = useCallback(() => {
    handleMenuClose();
    setPendingRename(true);
  }, [handleMenuClose]);

  const handleDelete = useCallback(() => {
    handleMenuClose();
    setIsDeleteDialogOpen(true);
  }, [handleMenuClose]);

  const handleDeleteConfirm = useCallback(async () => {
    try {
      await document.remove();
      showToast(texts.fileDeleted);
    } catch {
      showToast(texts.deleteError, { error: true });
    } finally {
      setIsDeleteDialogOpen(false);
    }
  }, [document, showToast]);

  const handleDeleteCancel = useCallback(() => setIsDeleteDialogOpen(false), []);

  const handleSave = useCallback(async () => {
    handleMenuClose();
    try {
      await document.toggleSaved(loggedInUserDoc?.id);
      showToast(isSaved ? texts.fileRemoved : texts.fileSaved);
    } catch {
      showToast(texts.saveError, { error: true });
    }
  }, [document, handleMenuClose, isSaved, showToast, loggedInUserDoc]);

  return (
    <>
      <MenuCell>
        {isRenaming ? (
          <RenameDoneButton
            type="button"
            data-rename-submit
            full={false}
            onMouseDown={(e) => e.preventDefault()}
            onClick={onRenameSubmit}
          >
            {texts.renameDone}
          </RenameDoneButton>
        ) : (
          <MenuIconButton onClick={handleMenuOpen} size="small">
            <MoreVertIcon fontSize="small" />
          </MenuIconButton>
        )}
      </MenuCell>
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        disableRestoreFocus
        TransitionProps={{ onExited: handleMenuExited }}
      >
        <MenuItem onClick={handleRename}>
          <EditIcon />
          {texts.rename}
        </MenuItem>
        {source === ClientDocumentSource.CHAT && (
          <MenuItem onClick={handleSave}>
            {isSaved ? <ArchiveIcon /> : <SaveIcon />}
            {isSaved ? texts.removeFromDocuments : texts.saveToDocuments}
          </MenuItem>
        )}
        <MenuItem onClick={handleDelete}>
          <TrashIcon />
          <DeleteText>{texts.delete}</DeleteText>
        </MenuItem>
      </Menu>
      <ConfirmDialog
        isOpen={isDeleteDialogOpen}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        /*
          Overridable because the generic copy cannot say what a delete costs
          elsewhere: a library document is withdrawn from every package it was
          published to, and the coach should be told how many before confirming.
        */
        dialogTexts={{
          title: deleteTexts.title || texts.deleteConfirmTitle,
          content: deleteTexts.content || texts.deleteConfirmContent,
          confirm: deleteTexts.confirm || texts.deleteConfirmButton,
          cancel: deleteTexts.cancel || texts.deleteConfirmCancel,
        }}
      />
    </>
  );
};

DocumentItemMenu.propTypes = {
  /*
    Not instanceOf(ClientDocument): Library Documents render the same row from
    product/{id}/libraryDocs, a different collection with the same surface. The
    shape is what keeps those two from drifting — a renamed getter on either side
    fails here rather than rendering blank.
  */
  document: PropTypes.shape({
    fileName: PropTypes.string,
    mimeType: PropTypes.string,
    type: PropTypes.string,
    uploadedAt: PropTypes.instanceOf(Date),
    uploadedBy: PropTypes.string,
    uploadedByName: PropTypes.string,
    assetPath: PropTypes.string,
    fileSize: PropTypes.number,
    source: PropTypes.string,
    isSaved: PropTypes.bool,
    rename: PropTypes.func,
    remove: PropTypes.func,
    toggleSaved: PropTypes.func,
  }).isRequired,
  isRenaming: PropTypes.bool.isRequired,
  onRenameStart: PropTypes.func.isRequired,
  onRenameSubmit: PropTypes.func.isRequired,
  /** Overrides for the delete confirmation; each field falls back to the shared copy. */
  deleteTexts: PropTypes.shape({
    title: PropTypes.string,
    content: PropTypes.string,
    confirm: PropTypes.string,
    cancel: PropTypes.string,
  }),
};

DocumentItemMenu.defaultProps = {
  deleteTexts: {},
};

export default DocumentItemMenu;
